// Period close (rule 16, design flag 11) and baseline lock/unlock (rule 21, V36-V37).
import { useMutation } from "convex/react";
import { useState } from "react";
import { Lock, Unlock } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { useProject } from "@/hooks/project";
import { Badge, Button, Card, Field, Input, Modal, PageHeader, fmtDate, fmtIdx, fmtNum, fmtPct, useRun, useToast } from "@/components/ui";
import { group, metricsAt } from "@/engine/compute";

export function PeriodsPage() {
  const { project, periods, currentIdx, prep, b, issues, canEdit, isAdmin } = useProject();
  const close = useMutation(api.periods.closeCurrent);
  const lock = useMutation(api.periods.lockBaseline);
  const unlock = useMutation(api.periods.unlockBaseline);
  const run = useRun();
  const toast = useToast();
  const [confirmClose, setConfirmClose] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [pw, setPw] = useState({ a: "", b: "", reason: "" });
  const reopen = useMutation(api.periods.reopenFrom);
  const [reopenN, setReopenN] = useState<number | null>(null);
  const [reopenWhy, setReopenWhy] = useState("");
  // Pre-lock checks: warn, don't block.
  const dlvHours = b.deliverables.filter((d) => (d.unit ?? "h") === "h").reduce((s, d) => s + d.weight, 0);
  const budgetHours = b.budgetCells.reduce((s, c) => s + c.hours, 0);
  const noPlanned = b.deliverables.filter((d) => !d.steps.some((s) => s.planned)).length;
  const lockWarnings = [
    !b.periods.length && "There's no calendar yet.",
    !b.deliverables.length && "There are no deliverables yet.",
    noPlanned > 0 && `${noPlanned} deliverable${noPlanned > 1 ? "s have" : " has"} no planned dates.`,
    !b.budgetCells.length && "The main budget is empty.",
    b.budgetCells.length > 0 && Math.abs(budgetHours - dlvHours) > 0.005 && `The budget (${fmtNum(budgetHours)} h) doesn't match the deliverables (${fmtNum(dlvHours)} h).`,
  ].filter(Boolean) as string[];
  const cur = periods[currentIdx];
  const open = cur && !cur.closed;
  const m = cur ? metricsAt(group(prep, { by: [] }).total, currentIdx, project.settings) : null;
  const blocking = [...issues.values()].flat().filter((i) => i.severity === "error" && i.field === "earned" && ["skippedStep", "earnedBackwards", "beforeCalendar"].includes(i.code));
  const warnings = [...issues.values()].flat().filter((i) => i.code === "reforecast" || i.code === "earnedInFuture");
  const snaps = [...b.snapshots].sort((x, y) => y.periodNumber - x.periodNumber);
  return (
    <div className="max-w-5xl space-y-4">
      <PageHeader title="Periods & baseline" sub="Close each period after updating it. Closing freezes that period's numbers; later edits and change orders never restate it." />
      <div className="grid grid-cols-2 gap-4">
        <Card title="Current period">
          {!cur ? (
            <p className="text-sm text-slate-500">Set up the calendar first.</p>
          ) : !open ? (
            <p className="text-sm text-slate-500">All periods are closed. The calendar never extends; add periods only by re-importing it if the project continues.</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="text-base font-semibold">
                P{cur.number} · {fmtDate(cur.start)} to {fmtDate(cur.cutoff)}
              </div>
              {m && (
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Planned" v={fmtPct(m.pctPlanned)} />
                  <Stat label="Earned" v={fmtPct(m.pctEarned)} />
                  <Stat label="Actual hours" v={fmtNum(m.ac)} />
                  <Stat label="SPI" v={fmtIdx(m.spi)} />
                  <Stat label="CPI" v={fmtIdx(m.cpi)} />
                  <Stat label="EAC" v={fmtNum(m.eac1)} />
                </div>
              )}
              {blocking.length > 0 && <p className="text-red-600">{blocking.length} earned-date errors must be fixed before closing (see Progress update, filter "With issues").</p>}
              {warnings.length > 0 && <p className="text-amber-700">{warnings.length} warnings (passed forecasts not earned, or earned dates after the cutoff). They don't block closing.</p>}
              {canEdit && (
                <Button variant="primary" disabled={blocking.length > 0} onClick={() => setConfirmClose(true)}>
                  Close P{cur.number}
                </Button>
              )}
            </div>
          )}
        </Card>
        <Card title="Baseline">
          <div className="space-y-3 text-sm">
            {project.baselineLocked ? (
              <>
                <Badge tone="amber">
                  <Lock className="mr-1 size-3" /> Locked
                </Badge>
                <p className="text-slate-600">Planned dates, weights, the chain on each deliverable, the budget table and indirect budgets are frozen. Scope changes go through change orders.</p>
                {isAdmin && (
                  <Button onClick={() => setUnlockOpen(true)}>
                    <Unlock className="size-4" /> Unlock
                  </Button>
                )}
              </>
            ) : (
              <>
                <Badge>Not locked</Badge>
                <p className="text-slate-600">Lock the baseline once planned dates and the budget are agreed. Unlocking needs the password and is recorded in the audit log.</p>
                {isAdmin && (
                  <Button variant="primary" onClick={() => setLockOpen(true)}>
                    <Lock className="size-4" /> Lock baseline
                  </Button>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
      <Card title="Closed periods (frozen snapshots)" bodyClass="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-right text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Period</th>
              <th className="px-3 py-2">Budget</th>
              <th className="px-3 py-2">Planned</th>
              <th className="px-3 py-2">Earned</th>
              <th className="px-3 py-2">Actual</th>
              <th className="px-3 py-2">% earned</th>
              <th className="px-3 py-2">SPI</th>
              <th className="px-3 py-2">CPI</th>
              <th className="px-3 py-2">EAC</th>
              {isAdmin && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {snaps.map((s) => {
              const t = s.totals.h;
              const p = periods.find((x) => x.number === s.periodNumber);
              return (
                <tr key={s._id} className="num border-t border-slate-100 text-right">
                  <td className="px-3 py-1.5 text-left">
                    P{s.periodNumber} <span className="text-slate-500">{fmtDate(p?.cutoff)}</span>
                  </td>
                  <td className="px-3 py-1.5">{fmtNum(t.bac)}</td>
                  <td className="px-3 py-1.5">{fmtNum(t.pv)}</td>
                  <td className="px-3 py-1.5">{fmtNum(t.ev)}</td>
                  <td className="px-3 py-1.5">{fmtNum(t.ac)}</td>
                  <td className="px-3 py-1.5">{fmtPct(t.pctEarned)}</td>
                  <td className="px-3 py-1.5">{fmtIdx(t.spi)}</td>
                  <td className="px-3 py-1.5">{fmtIdx(t.cpi)}</td>
                  <td className="px-3 py-1.5">{fmtNum(t.eac1)}</td>
                  {isAdmin && (
                    <td className="px-3 py-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setReopenWhy("");
                          setReopenN(s.periodNumber);
                        }}
                      >
                        Reopen
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!snaps.length && <p className="p-4 text-center text-sm text-slate-500">No periods closed yet.</p>}
      </Card>

      <Modal
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={`Close P${cur?.number}?`}
        footer={
          <>
            <Button onClick={() => setConfirmClose(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={async () => {
                const n = await run(() => close({ projectId: project._id, expectedNumber: cur!.number }), `Closed P${cur!.number}`);
                if (n !== undefined) setConfirmClose(false);
              }}
            >
              Close period
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          This freezes P{cur?.number}: its earned dates and actual hours can't change afterwards, and later change orders won't restate it. The next period becomes current. This can't be undone.
        </p>
      </Modal>
      <Modal
        open={reopenN !== null}
        onClose={() => setReopenN(null)}
        title={`Reopen P${reopenN}?`}
        footer={
          <>
            <Button onClick={() => setReopenN(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={!reopenWhy.trim()}
              onClick={async () => {
                const n = await run(() => reopen({ projectId: project._id, fromNumber: reopenN!, reason: reopenWhy }), `Reopened from P${reopenN}`);
                if (n !== undefined) setReopenN(null);
              }}
            >
              Reopen
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            P{reopenN}
            {snaps.some((x) => x.periodNumber > (reopenN ?? 0)) ? " and every later closed period" : ""} become open again, so you can fix earned dates or enter missed actual hours, then close again. Periods before P{reopenN} stay frozen.
          </p>
          <Field label="Reason (goes in the audit log)">
            <Input value={reopenWhy} onChange={(e) => setReopenWhy(e.target.value)} placeholder="e.g. forgot to enter actual hours" autoFocus />
          </Field>
        </div>
      </Modal>
      <Modal
        open={lockOpen}
        onClose={() => setLockOpen(false)}
        title="Lock baseline"
        footer={
          <>
            <Button onClick={() => setLockOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={pw.a.length < 6 || pw.a !== pw.b}
              onClick={async () => {
                const ok = await run(() => lock({ projectId: project._id, password: pw.a }), "Baseline locked");
                if (ok !== undefined) {
                  setLockOpen(false);
                  setPw({ a: "", b: "", reason: "" });
                }
              }}
            >
              Lock
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Locking freezes planned dates, weights and the budget. After this, scope changes go through change orders.</p>
          {lockWarnings.length > 0 && (
            <ul className="list-inside list-disc rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {lockWarnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          <Field label="Unlock password" hint="At least 6 characters. You'll need it to unlock.">
            <Input type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} />
          </Field>
          <Field label="Repeat password">
            <Input type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} />
          </Field>
        </div>
      </Modal>
      <Modal
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        title="Unlock baseline"
        footer={
          <>
            <Button onClick={() => setUnlockOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!pw.a || !pw.reason.trim()}
              onClick={async () => {
                const r = await run(() => unlock({ projectId: project._id, password: pw.a, reason: pw.reason }));
                if (r?.ok) {
                  toast({ text: "Baseline unlocked", tone: "success" });
                  setUnlockOpen(false);
                  setPw({ a: "", b: "", reason: "" });
                } else if (r) toast({ text: `${r.error}. The attempt was logged.`, tone: "error" });
              }}
            >
              Unlock
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Password">
            <Input type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} />
          </Field>
          <Field label="Reason" hint="Recorded in the audit log">
            <Input value={pw.reason} onChange={(e) => setPw({ ...pw, reason: e.target.value })} />
          </Field>
        </div>
      </Modal>
    </div>
  );
}

function Stat({ label, v }: { label: string; v: string }) {
  return (
    <div className="rounded border border-slate-200 px-2 py-1.5">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="num font-semibold">{v}</div>
    </div>
  );
}
